import React, { useCallback, useState, createContext, useContext } from 'react';
import { translations, Language } from '../utils/translations';
import { ensureArray } from '../utils/safeArray';
import { OrgType } from '../schemas/types';
import type { Organization, HierarchyLabelConfig } from '../schemas/types';
import { DEFAULT_HIERARCHY_LABELS } from '../schemas/mockData';
export const ORGANIZATIONS: Organization[] = [{
  id: 'org-kmg',
  name: 'Kyiv Mental Guard',
  spacesOrgId: 'KS-ORG-KMG-001',
  orgType: 'K12',
  defaultChatbotId: 'KS-BOT-KMG-01',
  customHierarchyLabels: {
    subOrganization: 'School',
    group: 'Grade',
    subGroup: 'Classroom'
  }
}, {
  id: 'org-letgrow',
  name: 'LetGrow',
  spacesOrgId: 'KS-ORG-LETGROW-001',
  orgType: 'Nonprofit',
  defaultChatbotId: 'KS-BOT-LETGROW-01',
  customHierarchyLabels: {
    subOrganization: 'School',
    group: 'Grade Band',
    subGroup: 'Role Group'
  }
}, {
  id: 'org-mvcc',
  name: 'Mohawk Valley Community College',
  spacesOrgId: 'KS-ORG-MVCC-001',
  orgType: 'College',
  defaultChatbotId: 'KS-BOT-MVCC-01',
  customHierarchyLabels: {
    subOrganization: 'Department',
    group: 'Course',
    subGroup: 'Section'
  }
}, {
  id: 'org-dicks',
  name: "Dick's Sporting Goods",
  spacesOrgId: 'KS-ORG-DICKS-001',
  orgType: 'Corporate',
  customHierarchyLabels: {
    subOrganization: 'Store',
    group: 'Department',
    subGroup: 'Job Title'
  }
}, {
  id: 'org-acme',
  name: 'Acme Supplies',
  spacesOrgId: 'KS-ORG-ACME-001',
  orgType: 'Corporate',
  customHierarchyLabels: {
    subOrganization: 'Department',
    group: 'Function',
    subGroup: 'Role'
  }
}];
interface OrganizationContextType {
  currentOrganization: Organization;
  setCurrentOrganization: (org: Organization) => void;
  language: Language;
  setLanguage: (lang: Language) => void;
  t: Record<string, string>;
  getHierarchyLabels: () => HierarchyLabelConfig;
  updateHierarchyLabels: (labels: Partial<HierarchyLabelConfig>) => void;
}
const OrganizationContext = createContext<OrganizationContextType | undefined>(undefined);
interface OrganizationProviderProps {
  children: ReactNode;
}
export function OrganizationProvider({
  children
}: OrganizationProviderProps) {
  // Ensure ORGANIZATIONS is always an array
  const safeOrganizations = ensureArray('OrganizationProvider.ORGANIZATIONS', ORGANIZATIONS);
  // Initialize with defaults immediately - use first org or create fallback
  const [currentOrganization, setCurrentOrganization] = useState<Organization>(safeOrganizations[0] || {
    id: 'org-default',
    name: 'Default Organization',
    spacesOrgId: 'KS-ORG-DEFAULT-001',
    orgType: 'K12'
  });
  const [language, setLanguage] = useState<Language>('en');
  // Get translations for current language
  const t = translations[language] || translations.en;
  const getHierarchyLabels = useCallback((): HierarchyLabelConfig => {
    const defaultLabels = DEFAULT_HIERARCHY_LABELS[currentOrganization.orgType];
    return currentOrganization.customHierarchyLabels || defaultLabels;
  }, [currentOrganization]);
  const updateHierarchyLabels = useCallback((labels: Partial<HierarchyLabelConfig>) => {
    const currentLabels = currentOrganization.customHierarchyLabels || DEFAULT_HIERARCHY_LABELS[currentOrganization.orgType];
    const updatedOrg = {
      ...currentOrganization,
      customHierarchyLabels: {
        ...currentLabels,
        ...labels
      }
    };
    // In real app, save to backend
    if (process.env.NODE_ENV === 'development') {
      console.log('Updated hierarchy labels:', updatedOrg.customHierarchyLabels);
    }
  }, [currentOrganization]);
  const value = {
    currentOrganization,
    setCurrentOrganization,
    language,
    setLanguage,
    t,
    getHierarchyLabels,
    updateHierarchyLabels
  };
  return <OrganizationContext.Provider value={value} data-id="element-585">
      {children}
    </OrganizationContext.Provider>;
}
export function useOrganization(): OrganizationContextType {
  const context = useContext(OrganizationContext);
  if (!context) {
    throw new Error('useOrganization must be used within an OrganizationProvider');
  }
  return context;
}